import { useMemo, useRef, useState } from "react";
import { Button } from "@/project/karuna/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { createForm } from "@formily/core";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";

const SCHEMA_NAME = "Activity Management";

type CreateActivityDialogProps = {
  onCreated?: () => void;
};

export function CreateActivityDialog({ onCreated }: CreateActivityDialogProps) {
  const [open, setOpen] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<{ handleSave: () => Promise<void> }>(null);
  // New Formily instance whenever formResetKey changes — otherwise values survive after create
  const form = useMemo(() => {
    void formResetKey;
    return createForm({});
  }, [formResetKey]);

  const openCreateDialog = () => {
    setFormResetKey(k => k + 1);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!formRef.current) {
      return;
    }
    setLoading(true);
    try {
      await formRef.current.handleSave();
      onCreated?.();
      setOpen(false);
    } catch (_error: unknown) {
      // Error handled by backend / form
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className="gap-2 bg-gradient-to-r from-primary to-primary/80"
        onClick={openCreateDialog}
      >
        <Plus className="h-4 w-4" />
        Create New Activity
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-visible p-0 sm:rounded-lg">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                Create New Activity
              </DialogTitle>
              <DialogDescription>
                Add a new activity with details, dates, and media files
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 pb-4">
              <SchemaFormAutoSubmit
                key={formResetKey}
                ref={formRef}
                form={form}
                schemaName={SCHEMA_NAME}
              />
            </div>
          </div>

          <DialogFooter className="border-t border-border bg-card px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-gradient-to-r from-primary to-primary/80 text-white hover:from-primary/90 hover:to-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Activity"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
